/***
 *
 *   PUBLIC TICKET FORM
 *   Phase 13 — port of ui-reference PublicTicketForm. Two-step intake
 *   (contact → request) with category + priority and a file dropzone.
 *   Posts to /api/ticket. Also used in-app via TicketForm wrapper.
 *
 *   When the contact step is already fully known — signed-in user, or a
 *   verified portal identity — the form opens directly on the request step.
 *   Unsigned ?fn/ln/e= prefills stay editable and do not skip the step.
 *
 *   PROPS
 *   categories: ticket category list
 *   category: optional pre-selected category id
 *   contact: false hides the contact step entirely (in-app, where the
 *     reporter is the signed-in user and has nothing to fill in)
 *   lockedContact: verified { first_name, last_name, email } from a signed
 *     support link — skips the contact step and binds ticket identity
 *   portalParams: { exp, sig } appended on submit so the server can re-verify
 *   cancel: optional callback — shows Cancel on the first visible step
 *   callback: called after successful submit
 *
 **********/

import { useId, useRef, useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Icon, cn } from 'components/lib';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import { PRIORITY_STYLES } from 'components/chip/chip.tailwind.js';
import Style, { PRIORITY_RING } from './publicTicketForm.tailwind.js';

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const STEP_ICONS = ['user', 'message-square'];

// Field order per step, so a failed validation can focus the first bad input
// rather than leaving the user to hunt for the red text.
const STEP_FIELDS = {
    1: ['first_name', 'last_name', 'email'],
    2: ['ticket_category_id', 'subject', 'priority'],
};

const emptyValues = {
    first_name: '',
    last_name: '',
    email: '',
    ticket_category_id: '',
    subject: '',
    description: '',
    priority: 'medium',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PublicTicketForm(props) {
    const { _t } = useTranslate();
    const { callApi } = useApiContext();
    const { user } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const formId = useId();
    const fileInputId = `${formId}-files`;
    const formRef = useRef(null);

    const categories = props.categories || [];

    const initialContact = {
        first_name: user?.first_name || props.lockedContact?.first_name || searchParams.get('fn') || '',
        last_name: user?.last_name || props.lockedContact?.last_name || searchParams.get('ln') || '',
        email: user?.email || props.lockedContact?.email || searchParams.get('e') || '',
    };

    // The contact step is dropped outright in-app (contact={false}), and
    // skipped when the reporter is already known — a signed-in user, or a
    // verified support-link identity. Unsigned query prefills stay on step 1.
    const showContactStep = props.contact !== false && !user && !props.lockedContact;
    const initialStep = showContactStep ? 1 : 2;

    // Nothing to step between once the contact step is gone, so the indicator
    // and the Back button drop out with it.
    const singleStep = !showContactStep;

    const [step, setStep] = useState(initialStep);
    const [files, setFiles] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [values, setValues] = useState({
        ...emptyValues,
        ...initialContact,
        ticket_category_id: props.category && props.category !== 'all' ? props.category : '',
    });

    useEffect(() => {
        if (!props.lockedContact) return;
        setValues((prev) => ({ ...prev, ...props.lockedContact }));
        setStep(2);
    }, [props.lockedContact]);

    function setField(name, value) {
        setValues((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => {
            if (!prev[name]) return prev;
            const next = { ...prev };
            delete next[name];
            return next;
        });
    }

    // Moves the caret to whichever field failed first. Without this the only
    // feedback on a long form is red text that may well be off-screen.
    function focusFirstError(found, whichStep) {
        const first = STEP_FIELDS[whichStep].find((name) => found[name]);
        if (!first) return;
        const el = formRef.current?.querySelector(`[name="${first}"]`);
        el?.focus();
    }

    function validateStep1() {
        const next = {};
        if (!values.first_name.trim()) next.first_name = _t('publicIntake.form.required');
        if (!values.last_name.trim()) next.last_name = _t('publicIntake.form.required');
        if (!values.email.trim()) next.email = _t('publicIntake.form.required');
        else if (!EMAIL_RE.test(values.email.trim())) {
            next.email = _t('publicIntake.form.emailInvalid');
        }
        setErrors(next);
        if (Object.keys(next).length) {
            focusFirstError(next, 1);
            return false;
        }
        return true;
    }

    function validateStep2() {
        const next = {};
        if (!values.ticket_category_id || values.ticket_category_id === 'unselected') {
            next.ticket_category_id = _t('publicIntake.form.required');
        }
        if (!values.subject.trim()) next.subject = _t('publicIntake.form.required');
        if (!values.priority) next.priority = _t('publicIntake.form.required');
        setErrors(next);
        if (Object.keys(next).length) {
            focusFirstError(next, 2);
            return false;
        }
        return true;
    }

    function goToStep2() {
        if (validateStep1()) setStep(2);
    }

    function addFiles(list) {
        if (!list?.length) return;
        setFiles((prev) => [...prev, ...Array.from(list)]);
    }

    function removeFile(index) {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    }

    async function submitForm() {
        if (!validateStep2()) return;

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('first_name', values.first_name.trim());
            formData.append('last_name', values.last_name.trim());
            formData.append('email', values.email.trim());
            formData.append('ticket_category_id', values.ticket_category_id);
            formData.append('subject', values.subject.trim());
            formData.append('description', values.description.trim());
            formData.append('priority', values.priority);
            if (props.portalParams?.sig) {
                formData.append('exp', props.portalParams.exp);
                formData.append('sig', props.portalParams.sig);
            }
            files.forEach((file) => formData.append('files', file));

            const res = await callApi({
                method: 'POST',
                url: '/api/ticket',
                data: formData,
                auth: false,
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (res.status === 200 || res.status === 201) {
                setValues({ ...emptyValues, ...initialContact });
                setFiles([]);
                setStep(initialStep);
                setErrors({});
                props.callback && props.callback(res);
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                submitForm();
            }}
            className={Style.form}
            noValidate>
            {showContactStep && (
                <StepIndicator
                    ariaLabel={_t('publicIntake.form.stepsProgressLabel')}
                    currentStep={step}
                    onStepClick={setStep}
                    stepStatus={(current, total) =>
                        _t('publicIntake.form.stepStatus', { current, total })
                    }
                    goToStepLabel={(label) => _t('publicIntake.form.goToStep', { label })}
                    labels={[
                        _t('publicIntake.form.steps.contact'),
                        _t('publicIntake.form.steps.request'),
                    ]}
                />
            )}

            {showContactStep && step === 1 && (
                <div className={Style.stepBlock}>
                    <div className={Style.grid2}>
                        <Field
                            id={`${formId}-first`}
                            label={_t('publicIntake.form.firstName')}
                            error={errors.first_name}>
                            {(a11y) => (
                                <input
                                    {...a11y}
                                    name="first_name"
                                    className={cn(
                                        Style.input,
                                        errors.first_name && Style.inputInvalid,
                                    )}
                                    autoComplete="given-name"
                                    required
                                    value={values.first_name}
                                    onChange={(e) => setField('first_name', e.target.value)}
                                />
                            )}
                        </Field>
                        <Field
                            id={`${formId}-last`}
                            label={_t('publicIntake.form.lastName')}
                            error={errors.last_name}>
                            {(a11y) => (
                                <input
                                    {...a11y}
                                    name="last_name"
                                    className={cn(
                                        Style.input,
                                        errors.last_name && Style.inputInvalid,
                                    )}
                                    autoComplete="family-name"
                                    required
                                    value={values.last_name}
                                    onChange={(e) => setField('last_name', e.target.value)}
                                />
                            )}
                        </Field>
                    </div>

                    <Field
                        id={`${formId}-email`}
                        label={_t('publicIntake.form.reporterEmail')}
                        error={errors.email}>
                        {(a11y) => (
                            <input
                                {...a11y}
                                name="email"
                                type="email"
                                className={cn(Style.input, errors.email && Style.inputInvalid)}
                                autoComplete="email"
                                required
                                value={values.email}
                                onChange={(e) => setField('email', e.target.value)}
                            />
                        )}
                    </Field>

                    <div className={Style.actions}>
                        {props.cancel && (
                            <Button
                                type="button"
                                variant="secondary"
                                text={_t('cancel')}
                                action={props.cancel}
                            />
                        )}
                        <Button
                            type="button"
                            variant="accent"
                            text={_t('publicIntake.form.continue')}
                            action={goToStep2}
                        />
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className={Style.stepBlock}>
                    {props.lockedContact && (
                        <p className={Style.lockedNote}>
                            {_t('publicIntake.form.identityLocked')}
                        </p>
                    )}
                    <div className={Style.step2Grid}>
                        <div className={Style.step2Col}>
                            <Field
                                id={`${formId}-subject`}
                                label={_t('publicIntake.form.subject')}
                                error={errors.subject}>
                                {(a11y) => (
                                    <input
                                        {...a11y}
                                        name="subject"
                                        className={cn(
                                            Style.input,
                                            errors.subject && Style.inputInvalid,
                                        )}
                                        required
                                        value={values.subject}
                                        onChange={(e) => setField('subject', e.target.value)}
                                        placeholder={_t(
                                            'publicIntake.form.subjectPlaceholder',
                                        )}
                                    />
                                )}
                            </Field>

                            <Field
                                id={`${formId}-description`}
                                label={_t('publicIntake.form.message')}
                                optional={_t('publicIntake.form.optional')}>
                                {(a11y) => (
                                    <textarea
                                        {...a11y}
                                        name="description"
                                        className={Style.textarea}
                                        rows={5}
                                        value={values.description}
                                        onChange={(e) =>
                                            setField('description', e.target.value)
                                        }
                                        placeholder={_t(
                                            'publicIntake.form.messagePlaceholder',
                                        )}
                                    />
                                )}
                            </Field>
                        </div>

                        <div className={Style.step2Col}>
                            <Field
                                id={`${formId}-category`}
                                label={_t('publicIntake.form.category')}
                                error={errors.ticket_category_id}
                                hint={
                                    !categories.length
                                        ? _t('publicIntake.form.noCategoriesHint')
                                        : undefined
                                }>
                                {(a11y) => (
                                    <select
                                        {...a11y}
                                        name="ticket_category_id"
                                        className={cn(
                                            Style.select,
                                            errors.ticket_category_id && Style.inputInvalid,
                                        )}
                                        value={values.ticket_category_id}
                                        disabled={!categories.length}
                                        required
                                        onChange={(e) =>
                                            setField('ticket_category_id', e.target.value)
                                        }>
                                        <option value="">
                                            {!categories.length
                                                ? _t('publicIntake.form.noCategories')
                                                : _t('publicIntake.form.category')}
                                        </option>
                                        {categories.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </Field>

                            <fieldset className="space-y-2">
                                <legend className={Style.label}>
                                    {_t('publicIntake.form.priority')}
                                </legend>
                                <div className={Style.priorityList}>
                                    {PRIORITIES.map((p) => (
                                        <PriorityCard
                                            key={p}
                                            priority={p}
                                            selected={values.priority === p}
                                            title={_t(`priority.${p}`)}
                                            help={_t(`publicIntake.priorityHelp.${p}`)}
                                            onSelect={() => setField('priority', p)}
                                        />
                                    ))}
                                </div>
                                <p className={Style.priorityGuidance}>
                                    {_t('publicIntake.form.priorityGuidance')}
                                </p>
                                {errors.priority && (
                                    <p className={Style.error} role="alert">
                                        {errors.priority}
                                    </p>
                                )}
                            </fieldset>
                        </div>
                    </div>

                    {/* Full width: a drop target is easier to hit wide, and the
                        priority column is far shorter than the text fields, so
                        keeping this in the left column left a gap beside it. */}
                    <div className="space-y-2">
                        <div className={Style.labelRow}>
                            <span className={Style.label}>
                                {_t('publicIntake.form.attachments')}
                            </span>
                            <span className={Style.optional}>
                                {_t('publicIntake.form.optional')}
                            </span>
                        </div>
                        {/* The input is the peer and the styled div is its
                            sibling, so a keyboard user gets a visible ring —
                            an sr-only input alone focuses invisibly. */}
                        <label htmlFor={fileInputId} className="block">
                            <input
                                id={fileInputId}
                                type="file"
                                className="peer sr-only"
                                multiple
                                onChange={(e) => {
                                    addFiles(e.target.files);
                                    e.target.value = '';
                                }}
                            />
                            <div
                                className={cn(
                                    Style.dropzone,
                                    dragging && Style.dropzoneActive,
                                )}
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragging(true);
                                }}
                                onDragEnter={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragging(true);
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragging(false);
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setDragging(false);
                                    addFiles(e.dataTransfer.files);
                                }}>
                                <Icon
                                    image="upload"
                                    size={18}
                                    color="currentColor"
                                    className={Style.dropzoneIcon}
                                />
                                <span className={Style.dropzoneText}>
                                    {_t('publicIntake.form.dropFiles')}
                                </span>
                                <span className={Style.dropzoneHint}>
                                    {_t('publicIntake.form.addFiles')}
                                </span>
                            </div>
                        </label>
                        {files.length > 0 && (
                            <>
                                <p className={Style.fileListLabel}>
                                    {_t('publicIntake.form.attachedFiles')} ({files.length})
                                </p>
                                <ul className={Style.fileList}>
                                    {files.map((file, i) => (
                                        <li key={`${file.name}-${i}`} className={Style.fileRow}>
                                            <Icon
                                                image="paperclip"
                                                size={14}
                                                color="currentColor"
                                                className={Style.fileIcon}
                                            />
                                            <span className={Style.fileName} title={file.name}>
                                                {file.name}
                                            </span>
                                            <span className={Style.fileSize}>
                                                {formatFileSize(file.size)}
                                            </span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                icon="x"
                                                iconButton
                                                ariaLabel={`${_t(
                                                    'publicIntake.form.removeFile',
                                                )} — ${file.name}`}
                                                action={() => removeFile(i)}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>

                    <div className={Style.actions}>
                        {!singleStep ? (
                            <Button
                                type="button"
                                variant="secondary"
                                text={_t('publicIntake.form.back')}
                                action={() => setStep(1)}
                            />
                        ) : (
                            props.cancel && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    text={_t('cancel')}
                                    action={props.cancel}
                                />
                            )
                        )}
                        <Button
                            type="button"
                            variant="accent"
                            text={_t('publicIntake.form.submit')}
                            isLoading={loading}
                            action={submitForm}
                            disabled={!categories.length}
                        />
                    </div>
                </div>
            )}
        </form>
    );
}

/* Children is a render prop so the field owns the wiring that ties its label,
   hint and error message back to the control — aria-describedby has to name
   ids the Field generates. */
function Field(props) {
    const hintId = `${props.id}-hint`;
    const errorId = `${props.id}-error`;
    const describedBy =
        [props.hint && hintId, props.error && errorId].filter(Boolean).join(' ') || undefined;

    return (
        <div className={Style.field}>
            <div className={Style.labelRow}>
                <label htmlFor={props.id} className={Style.label}>
                    {props.label}
                </label>
                {props.optional && <span className={Style.optional}>{props.optional}</span>}
            </div>
            {props.children({
                id: props.id,
                'aria-describedby': describedBy,
                'aria-invalid': props.error ? true : undefined,
            })}
            {props.hint && <p id={hintId} className={Style.hint}>{props.hint}</p>}
            {props.error && (
                <p id={errorId} className={Style.error} role="alert">
                    <Icon
                        image="alert-circle"
                        size={13}
                        color="currentColor"
                        className={Style.errorIcon}
                    />
                    {props.error}
                </p>
            )}
        </div>
    );
}

function PriorityCard(props) {
    const styles = PRIORITY_STYLES[props.priority];

    return (
        <label className={Style.priorityLabel}>
            <input
                type="radio"
                className="peer sr-only"
                name="priority"
                value={props.priority}
                checked={props.selected}
                onChange={props.onSelect}
            />
            <span
                className={cn(
                    Style.priorityCard,
                    styles.tint,
                    props.selected &&
                        cn(Style.priorityCardSelected, PRIORITY_RING[props.priority]),
                )}>
                <span className={cn(Style.priorityDot, styles.dot)} aria-hidden />
                <span className="min-w-0 flex-1">
                    <span className={Style.priorityTitle}>{props.title}</span>
                    <span className={Style.priorityHelp}>{props.help}</span>
                </span>
                {/* Inside the tinted span so it picks up the priority colour
                    from currentColor. A ring alone is a weak "selected" signal
                    when the four cards are already four different colours. */}
                {props.selected && (
                    <Icon
                        image="check"
                        size={14}
                        color="currentColor"
                        className={Style.priorityCheck}
                    />
                )}
            </span>
        </label>
    );
}

function StepIndicator(props) {
    const { currentStep, labels, ariaLabel, onStepClick } = props;

    return (
        <div className={Style.steps} aria-label={ariaLabel}>
            {labels.map((label, i) => {
                const stepNum = i + 1;
                const isActive = currentStep === stepNum;
                const isDone = currentStep > stepNum;

                const circle = (
                    <div
                        className={cn(
                            Style.stepCircle,
                            isDone
                                ? Style.stepCircleDone
                                : isActive
                                ? Style.stepCircleActive
                                : Style.stepCircleIdle,
                        )}>
                        <Icon
                            image={isDone ? 'check' : STEP_ICONS[i]}
                            size={16}
                            color="currentColor"
                        />
                    </div>
                );

                return (
                    <div key={stepNum} className={cn('flex items-center', i === 0 && 'flex-1')}>
                        <div className={Style.stepCol} aria-current={isActive ? 'step' : undefined}>
                            {isDone ? (
                                <button
                                    type="button"
                                    className={Style.stepButton}
                                    onClick={() => onStepClick(stepNum)}
                                    aria-label={props.goToStepLabel(label)}>
                                    {circle}
                                </button>
                            ) : (
                                circle
                            )}
                            <span
                                className={cn(
                                    Style.stepLabel,
                                    isActive ? Style.stepLabelActive : Style.stepLabelIdle,
                                )}>
                                {label}
                            </span>
                            {isActive && (
                                <span className="sr-only">
                                    {props.stepStatus(stepNum, labels.length)}
                                </span>
                            )}
                        </div>
                        {i < labels.length - 1 && (
                            <div
                                className={cn(
                                    Style.stepConnector,
                                    isDone ? Style.stepConnectorDone : Style.stepConnectorIdle,
                                )}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
